<template>
  <div style="width: 100%">
    <el-upload
      ref="upload"
      drag
      :multiple="multiple"
      :limit="10"
      :accept="allowedFormats"
      :list-type="type"
      action="undefined"
      :disabled="disabled"
      :auto-upload="false"
      :file-list="fileList"
      :on-change="handleChange"
    >
      <el-icon>
        <UploadFilledElementIcon />
      </el-icon>
      <div class="el-upload__text">
        Drop file here or <em>click to upload</em>
      </div>
      <template #tip>
        <div class="el-upload__tip">
          {{ tooltip }} {{ multiple ? multipleTooltip : '' }}
        </div>
      </template>
      <template #file="{ file }">
        <div>
          <div @click="handlePreview(file)">
            <video
              v-if="isVideo(file)"
              class="el-upload-list__item-thumbnail"
              :src="file.url"
            />
            <img
              v-else
              class="el-upload-list__item-thumbnail"
              alt=""
              :src="file.url"
            />
            <a class="el-upload-list__item-name">
              {{ file.name }}
            </a>
          </div>
          <el-icon v-if="!disabled" @click="handleRemove(file)">
            <CloseElementIcon />
          </el-icon>
        </div>
      </template>
    </el-upload>
    <el-dialog v-model:visible="dialogVisible">
      <video
        v-if="isVideo(dialogFile)"
        width="100%"
        controls
        class="el-upload-list__item-thumbnail"
        :src="dialogUrl"
      />
      <img
        v-else
        width="100%"
        class="el-upload-list__item-thumbnail"
        alt=""
        :src="dialogUrl"
      />
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { ref, defineComponent } from 'vue'

type Preview = {
  name: string
  url: string
}

const VIDEO_FORMAT = 'video/mp4'

export default defineComponent({
  name: 'FileUpload',
  props: {
    thumb: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'picture'
    },
    tooltip: {
      type: String,
      default: ''
    },
    multipleTooltip: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    allowedFormats: {
      type: String,
      default: ''
    }
  },
  setup(props, context) {
    const upload = ref()
    const fileList = ref([])
    const dialogVisible = ref(false)
    const dialogUrl = ref('')
    const dialogFile = ref()

    if (props.thumb) {
      const isVideo = /\.mp4/.test(props.thumb)
      const preview = {
        name: props.thumb.split('/').pop(),
        url: props.thumb,
        raw: { type: isVideo ? VIDEO_FORMAT : '' }
      } as Preview
      fileList.value = [preview]
    }

    const handleChange = (file: any, list: any) => {
      context.emit('handleChange', file, upload, list)
    }

    const handlePreview = (file: any) => {
      dialogFile.value = file
      dialogUrl.value = file.url
      dialogVisible.value = true
    }

    const isVideo = (file: any) => {
      return file ? VIDEO_FORMAT === file.raw.type : false
    }

    const handleRemove = (file: any) => {
      upload.value.handleRemove(file)
    }

    return {
      fileList,
      handleChange,
      isVideo,
      dialogVisible,
      dialogUrl,
      handlePreview,
      handleRemove,
      upload,
      dialogFile
    }
  }
})
</script>

<style lang="scss" scoped>
:deep(.el-upload-list--picture, .el-upload-list--text) {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}

:deep(
  .el-upload-list--picture .el-upload-list__item,
  .el-upload-list--text .el-upload-list__item
) {
  height: 100px;
  max-width: 300px;
  margin-right: 5px;
}

:deep(.el-upload) {
  width: 100%;
}

:deep(
  .el-upload-list--picture .el-upload-list__item-name,
  .el-upload-list--text .el-upload-list__item-name
) {
  margin-top: 0;
}

:deep(.el-upload-dragger) {
  width: 100%;
}
</style>
